import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Bot, MessageCircle, ArrowLeft, Send, StopCircle, Loader2, Trash2, User, Pencil, Plus, Upload, FileText, GripVertical, X, Paperclip, Camera, Link, Mic, Copy, RefreshCw, Volume2, Globe, Settings, ChevronDown } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import client from '../api/client'
import toast from 'react-hot-toast'
import { useAuthStore } from '../store/authStore'
import { useTokenStore } from '../store/tokenStore'
import ChatSettingsPanel from '../components/ChatSettings'

interface CharacterInfo {
  id: string
  name: string
  emoji: string
  color: string
  system_prompt: string
  avatar_url?: string
  bio?: string
  voice_id?: string | null
  is_global?: boolean
  is_owner?: boolean
  sort_order?: number
}

interface ChatSessionItem {
  id: string
  title: string
  model: string
  character_id: string | null
  createdAt: string
  updatedAt: string
  tokensUsed: number
}

interface ChatMessage {
  id: string
  role: string
  content: string
  tokens: number
  model: string | null
  timestamp: string
}

const COLORS: Record<string, string> = {
  blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
  amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/30',
  purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
  red: 'from-red-500/20 to-red-600/10 border-red-500/30',
  green: 'from-green-500/20 to-green-600/10 border-green-500/30',
  indigo: 'from-indigo-500/20 to-indigo-600/10 border-indigo-500/30',
}

// Эмодзи для выбора
const EMOJI_LIST = [
  '😀','😃','😄','😁','😅','🤣','😂','🙂','😉','😊','😇','🥰','😍','🤩','😘','😗','😚','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','🤥','😌','😔','😪','🤤','😴','😷','🤒','🤕','🤢','🤮','🥴','😵','🤯','🤠','🥳','🥸','😎','🤓','🧐','😕','😟','🙁','😮','😯','😲','😳','🥺','😦','😧','😨','😰','😥','😢','😭','😱','😖','😣','😞','😓','😩','😫','🥱','😤','😡','😠','🤬','💀','☠️','💩','🤡','👹','👺','👻','👽','👾','🤖','🎃','😺','😸','😹','😻','😼','😽','🙀','😿','😾'
]

// ============================================================
// Emoji Picker
// ============================================================
function EmojiPicker({ value, onChange }: { value: string; onChange: (emoji: string) => void }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-xs text-gray-300 transition-colors w-full"
      >
        Эмодзи: {value}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-2 left-0 z-50 bg-[#1a1a3e] border border-white/10 rounded-xl p-2 shadow-2xl w-[300px]">
            <div className="max-h-[200px] overflow-y-auto grid grid-cols-8 gap-1">
              {EMOJI_LIST.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => { onChange(emoji); setOpen(false) }}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg text-lg hover:bg-white/10 transition-colors ${emoji === value ? 'bg-indigo-500/20 ring-1 ring-indigo-500/50' : ''}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ============================================================
// Character Editor Modal
// ============================================================
function CharacterEditor({
  character,
  onSave,
  onClose,
}: {
  character: CharacterInfo | null
  onSave: () => void
  onClose: () => void
}) {
  const [name, setName] = useState(character?.name || '')
  const [emoji, setEmoji] = useState(character?.emoji || '🤖')
  const [systemPrompt, setSystemPrompt] = useState(character?.system_prompt || '')
  const [bio, setBio] = useState(character?.bio || '')
  const [saving, setSaving] = useState(false)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string>(character?.avatar_url || '')
  const fileRef = useRef<HTMLInputElement>(null)
  const trainRef = useRef<HTMLInputElement>(null)

  const handleAvatarPick = (file: File) => {
    setAvatarFile(file)
    const url = URL.createObjectURL(file)
    setAvatarPreview(url)
  }

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Введите имя персонажа')
      return
    }
    setSaving(true)
    try {
      let charId = character?.id || ''

      if (character) {
        // Обновление
        await client.put(`/characters/${character.id}`, {
          name: name.trim(),
          emoji,
          system_prompt: systemPrompt,
          bio,
        })
      } else {
        // Создание
        const { data } = await client.post('/characters', {
          name: name.trim(),
          emoji,
          color: 'indigo',
          system_prompt: systemPrompt,
          bio,
        })
        charId = data.id
      }

      // Загружаем аватар отдельно, если выбран
      if (avatarFile && charId) {
        const formData = new FormData()
        formData.append('file', avatarFile)
        try {
          await client.post(`/characters/upload-avatar/${charId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        } catch {
          toast.error('Фото не удалось загрузить, персонаж сохранён')
        }
      }

      toast.success(character ? 'Персонаж сохранён' : 'Персонаж создан')
      onSave()
      onClose()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Ошибка сохранения')
    } finally {
      setSaving(false)
    }
  }

  const handleTrain = async (file: File) => {
    if (!character?.id) {
      toast.error('Сначала сохрани персонажа')
      return
    }
    const formData = new FormData()
    formData.append('file', file)
    try {
      const { data } = await client.post(`/characters/${character.id}/train`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      toast(data.message || 'Файл загружен', { icon: '📄' })
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Ошибка загрузки файла')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-[#12122a] rounded-2xl border border-white/10 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="text-sm font-semibold text-white">
            {character ? 'Редактировать' : 'Создать'} персонажа
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10 text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Фото / Аватар — первым блоком */}
          <div>
            <label className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5 block">Фото</label>
            <div className="flex items-center gap-4">
              <div className="relative w-20 h-20 rounded-2xl overflow-hidden border border-white/10 bg-white/5 flex items-center justify-center">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-3xl text-gray-500">{emoji}</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-xs text-gray-300 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Загрузить фото
                </button>
                <EmojiPicker value={emoji} onChange={setEmoji} />
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleAvatarPick(f); e.target.value = '' }}
              />
            </div>
          </div>

          {/* Имя */}
          <div>
            <label className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5 block">Имя персонажа</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например: Эйнштейн"
              className="input-field w-full text-sm"
            />
          </div>

          {/* Описание */}
          <div>
            <label className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5 block">Описание</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={2}
              placeholder="Краткое описание персонажа"
              className="input-field w-full text-sm resize-none"
            />
          </div>

          {/* System Prompt */}
          <div>
            <label className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5 block">System Prompt</label>
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={5}
              placeholder="Опиши характер, манеру общения, background персонажа..."
              className="input-field w-full text-sm resize-none font-mono"
            />
          </div>

          {/* Обучающий файл */}
          {character?.id && (
            <div>
              <label className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5 block">Обучение</label>
              <button
                onClick={() => trainRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-xs text-gray-300 transition-colors"
              >
                <FileText className="w-4 h-4" />
                Загрузить файл (.txt, .md, .csv)
              </button>
              <input
                ref={trainRef}
                type="file"
                accept=".txt,.md,.csv,.json,.yaml,.yml"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleTrain(f); e.target.value = '' }}
              />
              <p className="text-[10px] text-gray-600 mt-1">Текст файла будет добавлен в system prompt персонажа</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs text-gray-300 transition-colors">
            Отмена
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-4 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-xs text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (character ? 'Сохранить' : 'Создать')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Character Chat
// ============================================================
function CharacterChat({
  character,
  onBack,
}: {
  character: CharacterInfo
  onBack: () => void
}) {
  const { token: authToken } = useAuthStore()
  const { balance } = useTokenStore()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [initLoading, setInitLoading] = useState(true)
  const [showAttachMenu, setShowAttachMenu] = useState(false)
  const [showSettingsPanel, setShowSettingsPanel] = useState(false)
  const [files, setFiles] = useState<{name: string; base64: string}[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const recognitionRef = useRef<any>(null)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [speakingMsgId, setSpeakingMsgId] = useState<string | null>(null)
  const [webSearch, setWebSearch] = useState(false)
  const [hoveredMsgId, setHoveredMsgId] = useState<string | null>(null)
  const [editMessageId, setEditMessageId] = useState<string | null>(null)
  const [editMessageContent, setEditMessageContent] = useState('')
  const [cameraModal, setCameraModal] = useState(false)
  const streamRef = useRef<MediaStream | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [showVoiceMenu, setShowVoiceMenu] = useState(false)
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([])
  const [selectedVoiceName, setSelectedVoiceName] = useState<string | null>(null)
  const [serverModels, setServerModels] = useState<any[]>([])
  const [selectedModel, setSelectedModel] = useState('')
  const [showModelSelector, setShowModelSelector] = useState(false)
  const [userModelsEnabled, setUserModelsEnabled] = useState<string[] | null>(null)

  // Загрузка моделей
  useEffect(() => {
    client.get('/models/ai-models').then(({ data }) => {
      const items = (data || []).filter((m: any) => m.is_active && m.category === 'chat')
      setServerModels(items)
      const free = items.filter((m: any) => !m.is_paid)
      if (free.length > 0) setSelectedModel(free[0].model_name)
    }).catch(() => {})
  }, [])

  const visibleModels = useMemo(() => {
    const filtered = !userModelsEnabled
      ? serverModels
      : serverModels.filter(m => userModelsEnabled.includes(m.model_name))
    const free = filtered
      .filter(m => !m.is_paid)
      .map(m => ({ ...m, icon: 'FREE', color: 'text-emerald-400', sort: 0 }))
    const paid = filtered
      .filter(m => m.is_paid)
      .map(m => ({ ...m, icon: '👑', color: 'text-amber-400', sort: 1 }))
    return [...free, ...paid].sort((a, b) => a.sort - b.sort)
  }, [serverModels, userModelsEnabled])

  const SESSION_KEY = `char_session_${character.id}`
  const FREE_MODEL_KEY = 'char_free_model'

  // Получить первую бесплатную модель (из кэша или с сервера)
  const getFreeModel = async (): Promise<string> => {
    const cached = localStorage.getItem(FREE_MODEL_KEY)
    if (cached) return cached
    try {
      const { data } = await client.get('/models/first-free')
      const model = data?.model_name || 'gpt-5.4-mini'
      localStorage.setItem(FREE_MODEL_KEY, model)
      return model
    } catch {
      return 'gpt-5.4-mini'
    }
  }

  // Восстановление сессии из кэша
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const cachedSessionId = localStorage.getItem(SESSION_KEY)
      if (cachedSessionId) {
        try {
          const { data } = await client.get(`/chat/${cachedSessionId}`)
          if (!cancelled && data) {
            // Фильтруем пустые assistant сообщения
            const cleanMsgs = (data.messages || []).filter(
              (m: ChatMessage) => m.role !== 'assistant' || m.content?.trim()
            )
            setSessionId(data.id)
            setMessages(cleanMsgs)
            setInitLoading(false)
            return
          }
        } catch {
          localStorage.removeItem(SESSION_KEY)
        }
      }

      const modelName = selectedModel || (await getFreeModel())
      try {
        const { data } = await client.post('/chat', {
          model: modelName,
          title: `Чат с ${character.name}`,
          character_id: character.id,
        })
        if (!cancelled) {
          setSessionId(data.id)
          localStorage.setItem(SESSION_KEY, data.id)
        }
      } catch {
        toast.error('Ошибка при создании чата')
      }
      if (!cancelled) setInitLoading(false)
    })()
    return () => { cancelled = true }
  }, [character.id])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 260) + 'px'
    }
  }, [input])

  // Загрузка голосов
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices()
      if (voices.length > 0) {
        const ranked = voices
          .filter(v => v.lang.startsWith('ru') || v.lang.startsWith('en') || v.lang.startsWith('de') || v.lang.startsWith('fr') || v.lang.startsWith('es'))
          .sort((a, b) => {
            const aScore = /Microsoft|Google|Apple|Yandex/i.test(a.name) ? 3 : /Samantha|Karen|Daniel|Moira|Tessa/i.test(a.name) ? 2 : 1
            const bScore = /Microsoft|Google|Apple|Yandex/i.test(b.name) ? 3 : /Samantha|Karen|Daniel|Moira|Tessa/i.test(b.name) ? 2 : 1
            return bScore - aScore
          })
        setAvailableVoices(ranked)
        if (!selectedVoiceName && ranked.length > 0) {
          setSelectedVoiceName(ranked[0].name)
        }
      }
    }
    loadVoices()
    window.speechSynthesis.onvoiceschanged = loadVoices
    return () => { window.speechSynthesis.onvoiceschanged = null }
  }, [])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files
    if (!fileList) return

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i]
      const reader = new FileReader()
      reader.onload = (ev) => {
        const base64 = (ev.target?.result as string)?.split(',')[1] || ''
        setFiles(prev => [...prev, { name: file.name, base64 }])
      }
      reader.readAsDataURL(file)
    }
  }

  const handleCaptureCamera = () => {
    if (navigator.mediaDevices?.getUserMedia) {
      setCameraModal(true)
      setTimeout(async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
            audio: false
          })
          streamRef.current = stream
          if (videoRef.current) {
            videoRef.current.srcObject = stream
          }
        } catch (err) {
          console.error('Camera error:', err)
          setCameraModal(false)
          toast.error('Не удалось получить доступ к камере')
        }
      }, 100)
    } else {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.capture = 'environment'
      input.onchange = (e: any) => {
        if (e.target?.files?.length > 0) {
          handleFileSelect({ target: { files: e.target.files, value: '' } } as any)
        }
      }
      input.click()
    }
  }

  const handleAddPageLink = () => {
    const url = window.prompt('Введите ссылку на страницу:', 'https://')
    if (!url || !url.trim()) return
    setInput((prev) => {
      const trimmed = prev.trim()
      return trimmed ? `${trimmed}\n[Ссылка](${url.trim()})` : `[Ссылка](${url.trim()})`
    })
    inputRef.current?.focus()
  }

  // Голосовой ввод
  const startRecording = useCallback(() => {
    if (isRecording) {
      if (recognitionRef.current) {
        recognitionRef.current.stop()
      }
      setIsRecording(false)
      return
    }
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SpeechRecognition) {
      toast.error('Ваш браузер не поддерживает голосовой ввод')
      return
    }
    const recognition = new SpeechRecognition()
    recognition.lang = 'ru-RU'
    recognition.continuous = false
    recognition.interimResults = true
    recognition.maxAlternatives = 1
    recognition.onresult = (event: any) => {
      const results = event.results
      const lastResult = results[results.length - 1]
      if (lastResult && lastResult.isFinal) {
        const text = lastResult[0].transcript
        setInput(text)
      }
    }
    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error)
      if (event.error !== 'aborted') {
        toast.error('Ошибка распознавания речи')
      }
      setIsRecording(false)
    }
    recognition.onend = () => {
      setIsRecording(false)
    }
    recognitionRef.current = recognition
    recognition.start()
    setIsRecording(true)
  }, [isRecording])

  // Озвучивание текста
  const speakText = useCallback((text: string, msgId: string) => {
    if (isSpeaking && speakingMsgId === msgId) {
      window.speechSynthesis.cancel()
      setIsSpeaking(false)
      setSpeakingMsgId(null)
      return
    }
    window.speechSynthesis.cancel()
    const utterance = new SpeechSynthesisUtterance(text.replace(/[*#_\[\]()]/g, '').slice(0, 2000))
    utterance.lang = 'ru-RU'
    utterance.rate = 1.0
    utterance.pitch = 1.0
    const voices = window.speechSynthesis.getVoices()
    const ruVoice = voices.find(v => v.lang.startsWith('ru'))
    if (ruVoice) utterance.voice = ruVoice
    utterance.onstart = () => { setIsSpeaking(true); setSpeakingMsgId(msgId) }
    utterance.onend = () => { setIsSpeaking(false); setSpeakingMsgId(null) }
    utterance.onerror = () => { setIsSpeaking(false); setSpeakingMsgId(null) }
    window.speechSynthesis.speak(utterance)
  }, [isSpeaking, speakingMsgId])

  // Копирование в буфер
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Скопировано')
  }

  // Перегенерация ответа
  const regenerateMessage = (msg: ChatMessage) => {
    if (msg.role !== 'assistant') return
    const msgs = [...messages]
    let lastUserMsg: ChatMessage | null = null
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'user') {
        lastUserMsg = msgs[i]
        break
      }
    }
    if (!lastUserMsg || !sessionId) return
    setMessages(prev => prev.filter(m => m.id !== msg.id))
    // Отправляем новый запрос
    setInput('')
    setLoading(true)
    setIsSending(true)

    const bodyObj: any = { content: lastUserMsg.content, character_id: character.id, model: selectedModel }

    const controller = new AbortController()
    abortRef.current = controller

    fetch('/api/chat/' + sessionId + '/message/stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify(bodyObj),
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) {
        toast.error(`HTTP ${response.status}`)
        setLoading(false)
        return
      }
      const reader = response.body?.getReader()
      if (!reader) { setLoading(false); return }

      const decoder = new TextDecoder()
      let buffer = ''
      let fullContent = ''
      let assistantId: string | null = null
      let msgCreated = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i]
          if (line.startsWith('event: meta')) {
            const nl = lines[i + 1]
            if (nl?.startsWith('data: ')) {
              try {
                const meta = JSON.parse(nl.slice(6))
                if (meta.assistant_msg_id) assistantId = meta.assistant_msg_id
              } catch {}
            }
            i++
          } else if (line.startsWith('data: ')) {
            try {
              const payload = JSON.parse(line.slice(6))
              const chunkText = payload.content ?? payload.text
              if (chunkText !== undefined) {
                if (!msgCreated) {
                  msgCreated = true
                  setLoading(false)
                  fullContent = chunkText
                  const msgId = assistantId || ('stream-' + Date.now())
                  setMessages(prev => [...prev, { id: msgId, role: 'assistant', content: fullContent, tokens: 0, model: null, timestamp: new Date().toISOString() }])
                } else {
                  fullContent += chunkText
                  setMessages(prev =>
                    prev.map(m => m.id === assistantId ? { ...m, content: fullContent } : m)
                  )
                }
              }
            } catch {}
          }
        }
      }
    }).catch((e: any) => {
      if (e?.name !== 'AbortError') {
        toast.error(e?.message || 'Ошибка при отправке')
      }
      setLoading(false)
    }).finally(() => {
      setIsSending(false)
      abortRef.current = null
    })
  }

  // Удаление сообщения
  const deleteMessage = async (msgId: string) => {
    try {
      await client.delete(`/chat/message/${msgId}`)
      toast.success('Сообщение удалено')
      setMessages(prev => prev.filter(m => m.id !== msgId))
    } catch {
      setMessages(prev => prev.filter(m => m.id !== msgId))
      toast.success('Сообщение удалено')
    }
  }

  const handleSend = useCallback(async () => {
    const text = input.trim()
    if (!text && files.length === 0) return
    if (!sessionId) return
    setInput('')
    setLoading(true)
    setIsSending(true)

    const currentFiles = [...files]
    setFiles([])

    const displayContent = text || currentFiles.map(f => `[${f.name}]`).join(', ')
    const userMsg: ChatMessage = {
      id: 'temp-' + Date.now(),
      role: 'user',
      content: displayContent,
      tokens: 0,
      model: null,
      timestamp: new Date().toISOString(),
    }
    setMessages(prev => [...prev, userMsg])

    try {
      const bodyObj: any = { content: text, character_id: character.id, model: selectedModel }
      if (currentFiles.length > 0) {
        bodyObj.files = currentFiles.map(f => f.base64)
        bodyObj.filenames = currentFiles.map(f => f.name)
      }

      const controller = new AbortController()
      abortRef.current = controller

      const response = await fetch('/api/chat/' + sessionId + '/message/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify(bodyObj),
        signal: controller.signal,
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ detail: 'Ошибка запроса' }))
        toast.error(errData.detail || `HTTP ${response.status}`)
        setLoading(false)
        return
      }

      const reader = response.body?.getReader()
      if (!reader) {
        setLoading(false)
        return
      }

      const decoder = new TextDecoder()
      let buffer = ''
      let fullContent = ''
      let assistantId: string | null = null
      let msgCreated = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i]
          if (line.startsWith('event: meta')) {
            const nl = lines[i + 1]
            if (nl?.startsWith('data: ')) {
              try {
                const meta = JSON.parse(nl.slice(6))
                if (meta.assistant_msg_id) {
                  assistantId = meta.assistant_msg_id
                }
              } catch {}
            }
            i++
          } else if (line.startsWith('data: ')) {
            try {
              const payload = JSON.parse(line.slice(6))
              const chunkText = payload.content ?? payload.text
              if (chunkText !== undefined) {
                if (!msgCreated) {
                  msgCreated = true
                  setLoading(false)
                  fullContent = chunkText
                  const msgId = assistantId || ('stream-' + Date.now())
                  setMessages(prev => [...prev, { id: msgId, role: 'assistant', content: fullContent, tokens: 0, model: null, timestamp: new Date().toISOString() }])
                } else {
                  fullContent += chunkText
                  setMessages(prev =>
                    prev.map(m => m.id === assistantId ? { ...m, content: fullContent } : m)
                  )
                }
              }
            } catch {}
          }
        }
      }
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        // нормальная отмена
      } else {
        toast.error(e?.message || 'Ошибка при отправке')
      }
      setLoading(false)
    } finally {
      setIsSending(false)
      abortRef.current = null
    }
  }, [input, sessionId, character.id, files])

  const handleStop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
    }
    setLoading(false)
    setIsSending(false)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleClearDialogue = async () => {
    if (!sessionId) return
    try {
      await client.delete(`/chat/${sessionId}`)
    } catch {}
    localStorage.removeItem(SESSION_KEY)
    setMessages([])
    setSessionId(null)
    setInput('')
    const freeModel = localStorage.getItem('char_free_model') || 'gpt-5.4-mini'
    try {
      const { data } = await client.post('/chat', {
        model: freeModel,
        title: `Чат с ${character.name}`,
        character_id: character.id,
      })
      setSessionId(data.id)
      localStorage.setItem(SESSION_KEY, data.id)
      toast.success('Диалог очищен')
    } catch {
      toast.error('Ошибка при создании нового диалога')
    }
  }

  const fileImportRef = useRef<HTMLInputElement>(null)

  const handleImportChats = () => {
    fileImportRef.current?.click()
  }

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    for (let i = 0; i < files.length; i++) {
      try {
        const text = await files[i].text()
        const data = JSON.parse(text)
        if (Array.isArray(data) && data.length > 0) {
          setMessages(data)
          toast.success(`Импортировано ${data.length} сообщений`)
        } else {
          toast.error('Файл не содержит сообщений')
        }
      } catch (err: any) {
        toast.error(err.message || 'Ошибка импорта')
      }
    }

    e.target.value = ''
  }

  if (initLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-1rem)] max-w-4xl mx-auto">
      {/* Шапка */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 shrink-0">
        <button
          onClick={onBack}
          className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        {character.avatar_url ? (
          <img src={character.avatar_url} alt="" className="w-10 h-10 rounded-xl object-cover border border-white/10" />
        ) : (
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center text-xl">
            {character.emoji}
          </div>
        )}
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-white">{character.name}</h2>
          <p className="text-[11px] text-gray-500">{character.bio ? character.bio.slice(0, 60) : 'Персонаж'}</p>
        </div>
      </div>

      {/* Сообщения */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scrollbar-thin">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md">
              {character.avatar_url ? (
                <img src={character.avatar_url} alt="" className="w-20 h-20 rounded-2xl object-cover mx-auto mb-3 border border-white/10" />
              ) : (
                <div className="text-4xl mb-3">{character.emoji}</div>
              )}
              <p className="text-gray-400 text-sm">
                Начни разговор с <span className="text-white font-medium">{character.name}</span>
              </p>
              {character.bio && (
                <p className="text-gray-600 text-xs mt-2 max-w-xs mx-auto">{character.bio}</p>
              )}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-3 group relative ${
              msg.role === 'user' ? 'justify-end' : 'justify-start'
            }`}
            onMouseEnter={() => setHoveredMsgId(msg.id)}
            onMouseLeave={() => setHoveredMsgId(null)}
          >
            {msg.role === 'assistant' && (
              character.avatar_url ? (
                <img src={character.avatar_url} alt="" className="w-8 h-8 rounded-lg object-cover border border-white/10 shrink-0 mt-1" />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center text-sm shrink-0 mt-1">
                  {character.emoji}
                </div>
              )
            )}
            <div className="relative">
              <div
                className={`max-w-[75vw] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-indigo-500/20 text-white rounded-br-md'
                    : 'bg-white/5 text-gray-200 rounded-bl-md'
                }`}
              >
                {msg.role === 'assistant' ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    className="prose prose-invert prose-sm max-w-none leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_li]:mb-0.5 [&_code]:bg-white/10 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_pre]:bg-black/30 [&_pre]:p-3 [&_pre]:rounded-xl [&_pre]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-indigo-500/50 [&_blockquote]:pl-3 [&_blockquote]:text-gray-400 [&_blockquote]:italic [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-white/10 [&_th]:p-2 [&_th]:text-left [&_td]:border [&_td]:border-white/10 [&_td]:p-2 [&_table]:my-2 [&_a]:text-indigo-400 [&_a]:underline [&_a]:hover:text-indigo-300 [&_hr]:border-white/10 [&_hr]:my-3 [&_strong]:font-semibold [&_em]:italic [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_h1]:font-bold [&_h2]:font-semibold [&_h3]:font-semibold [&_h1]:mb-2 [&_h2]:mb-1.5 [&_h3]:mb-1 [&_h1]:mt-2 [&_h2]:mt-2 [&_h3]:mt-1"
                  >
                    {msg.content}
                  </ReactMarkdown>
                ) : (
                  msg.content
                )}
              </div>

              {/* Action buttons on hover */}
              {hoveredMsgId === msg.id && (
                <div className={`absolute -top-2 ${
                  msg.role === 'user' ? 'left-0' : 'right-0'
                } flex items-center gap-0.5 z-10`}>
                  {/* Edit — только для user */}
                  {msg.role === 'user' && (
                    <button
                      onClick={() => {
                        setEditMessageId(msg.id)
                        setEditMessageContent(msg.content)
                        setInput(msg.content)
                        inputRef.current?.focus()
                      }}
                      className="p-1 rounded-md bg-gray-800 border border-white/10 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
                      title="Редактировать"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}

                  {/* Copy */}
                  <button
                    onClick={() => copyToClipboard(msg.content)}
                    className="p-1 rounded-md bg-gray-800 border border-white/10 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
                    title="Копировать"
                  >
                    <Copy className="w-3 h-3" />
                  </button>

                  {/* Delete */}
                  <button
                    onClick={() => deleteMessage(msg.id)}
                    className="p-1 rounded-md bg-gray-800 border border-white/10 text-red-400 hover:text-red-300 hover:bg-red-900/30 transition-all"
                    title="Удалить"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>

                  {/* Regenerate — только для assistant */}
                  {msg.role === 'assistant' && (
                    <button
                      onClick={() => regenerateMessage(msg)}
                      className="p-1 rounded-md bg-gray-800 border border-white/10 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
                      title="Перегенерировать"
                    >
                      <RefreshCw className="w-3 h-3" />
                    </button>
                  )}

                  {/* Speak — только для assistant */}
                  {msg.role === 'assistant' && msg.content && (
                    <button
                      onClick={() => speakText(msg.content, msg.id)}
                      className={`p-1 rounded-md border transition-all ${
                        isSpeaking && speakingMsgId === msg.id
                          ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-400'
                          : 'bg-gray-800 border-white/10 text-gray-400 hover:text-white hover:bg-gray-700'
                      }`}
                      title={isSpeaking && speakingMsgId === msg.id ? 'Остановить' : 'Озвучить ответ'}
                    >
                      {isSpeaking && speakingMsgId === msg.id ? (
                        <StopCircle className="w-3 h-3" />
                      ) : (
                        <Volume2 className="w-3 h-3" />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
            {msg.role === 'user' && (
              <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-sm shrink-0 mt-1">
                <User className="w-4 h-4 text-indigo-300" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-3">
            {character.avatar_url ? (
              <img src={character.avatar_url} alt="" className="w-8 h-8 rounded-lg object-cover border border-white/10 shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center shrink-0">
                {character.emoji}
              </div>
            )}
            <div className="bg-white/5 rounded-2xl rounded-bl-md px-4 py-3">
              <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Поле ввода */}
      <div className="border-t border-white/10 px-4 py-3 shrink-0">
        {/* Панель над полем ввода: статус и кнопки */}
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="flex items-center gap-2 text-[10px] text-gray-600">
            <div className="relative">
              <button
                onClick={() => setShowSettingsPanel(!showSettingsPanel)}
                className="p-1 rounded-md text-gray-500 hover:text-indigo-400 hover:bg-white/5 transition-all"
                title="Настройки чата"
              >
                <Settings className="w-3.5 h-3.5" />
              </button>
              {showSettingsPanel && (
                <ChatSettingsPanel
                  onImport={handleImportChats}
                  onClose={() => setShowSettingsPanel(false)}
                />
              )}
            </div>
            {/* Model selector */}
            <div className="relative">
              <button
                onClick={() => setShowModelSelector(!showModelSelector)}
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg glass text-[10px] text-gray-400 hover:text-gray-200 transition-colors"
              >
                <Bot className="w-3 h-3" />
                {(() => {
                  const model = visibleModels.find(m => m.model_name === selectedModel)
                  if (!model) return <span className="text-gray-500">выбрать</span>
                  return (
                    <>
                      {model.is_paid ? (
                        <span className="text-[9px]">👑</span>
                      ) : (
                        <span className="text-[8px] font-bold text-emerald-400 bg-emerald-500/10 px-1 py-0.5 rounded">FREE</span>
                      )}
                      <span>{model.display_name || model.model_name}</span>
                    </>
                  )
                })()}
                <ChevronDown className="w-2.5 h-2.5 text-gray-600" />
              </button>
              {showModelSelector && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowModelSelector(false)} />
                  <div className="absolute bottom-full left-0 mb-1 w-64 glass-card z-20 p-1.5 space-y-0.5 max-h-64 overflow-y-auto">
                    {visibleModels.map((model) => {
                      const isPaidLocked = model.is_paid && (balance ?? 0) < 1000
                      return (
                      <button
                        key={model.model_name}
                        onClick={() => {
                          if (isPaidLocked) { toast.error('Недостаточно токенов. Пополните баланс для доступа к платным моделям.'); return }
                          setSelectedModel(model.model_name); setShowModelSelector(false)
                        }}
                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-[11px] transition-colors ${
                          selectedModel === model.model_name
                            ? 'bg-indigo-500/10 text-indigo-300'
                            : isPaidLocked
                              ? 'text-gray-500 cursor-not-allowed opacity-60'
                              : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5">
                            {model.is_paid ? (
                              <span className="text-[9px]">👑</span>
                            ) : (
                              <span className="text-[7px] font-bold text-emerald-400 bg-emerald-500/10 px-1 py-0.5 rounded">FREE</span>
                            )}
                            <span className={isPaidLocked ? 'text-gray-500' : ''}>{model.display_name || model.model_name}</span>
                          </span>
                        </div>
                      </button>
                      )
                    })}
                    {visibleModels.length === 0 && (
                      <div className="px-2.5 py-2 text-[10px] text-gray-500">Нет доступных моделей</div>
                    )}
                  </div>
                </>
              )}
            </div>
            {webSearch && (
              <>
                
                <Globe className="w-3 h-3" />
                <span>Поиск вкл</span>
              </>
            )}
            {files.length > 0 && (
              <>
                
                <Paperclip className="w-3 h-3" />
                <span>{files.length} файл(ов)</span>
              </>
            )}
          </div>
          {/* Вторая строка: голос + персонаж + токены — на мобилке перенос */}
          <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-500">
            <div className="relative">
              <button
                onClick={() => setShowVoiceMenu(!showVoiceMenu)}
                className="flex items-center gap-1 p-1 rounded-md hover:bg-white/5 transition-colors"
                title="Голос озвучки"
              >
                <Volume2 className="w-3 h-3 text-gray-500" />
                {selectedVoiceName && (
                  <span className="text-[9px] text-gray-500 max-w-[80px] overflow-hidden text-ellipsis whitespace-nowrap hidden sm:inline">
                    {selectedVoiceName.split('-').pop()?.replace(/[Nn]eural|[Nn]atural/g, '').trim() || selectedVoiceName.split(' ').pop()}
                  </span>
                )}
              </button>
              {showVoiceMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowVoiceMenu(false)} />
                  <div className="absolute bottom-full left-0 mb-1 w-56 glass-card z-20 p-1.5 space-y-0.5 max-h-48 overflow-y-auto">
                    {availableVoices.length === 0 ? (
                      <div className="px-2 py-1.5 text-[10px] text-gray-500">Нет голосов</div>
                    ) : (
                      availableVoices.map((v) => (
                        <button
                          key={v.name}
                          onClick={() => {
                            setSelectedVoiceName(v.name)
                            setShowVoiceMenu(false)
                          }}
                          className={`w-full text-left px-2 py-1.5 rounded-md text-[10px] transition-colors ${
                            selectedVoiceName === v.name
                              ? 'bg-indigo-500/10 text-indigo-300'
                              : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="truncate flex-1">{v.name}</span>
                            <span className="text-[8px] text-gray-600 ml-1">{v.lang}</span>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
            <span className="text-gray-500">персонаж</span>
            <span className="text-amber-400/80 font-medium">{balance.toLocaleString()}</span>
            <span className="text-gray-600">токенов</span>
          </div>
        </div>

        {/* Edit mode indicator */}
        {editMessageId && (
          <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
            <Pencil className="w-3.5 h-3.5 text-indigo-400" />
            <span className="text-xs text-indigo-300">Редактирование сообщения</span>
            <button
              onClick={() => { setEditMessageId(null); setEditMessageContent(''); setInput('') }}
              className="ml-auto p-0.5 rounded hover:bg-indigo-500/20 text-indigo-400"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Input field */}
        <div className="glass rounded-2xl p-1.5 border border-white/5 focus-within:border-indigo-500/30 transition-all">
          <div className="flex items-end gap-2">
            {/* Voice input button */}
            <button
              onClick={startRecording}
              className={`p-2 rounded-lg transition-all shrink-0 ${
                isRecording
                  ? 'bg-red-500/20 text-red-400 animate-pulse'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
              }`}
              title={isRecording ? 'Остановить запись' : 'Голосовой ввод'}
            >
              {isRecording ? <StopCircle className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>

            {/* Attach file button with menu */}
            <div className="relative">
              <button
                onClick={() => setShowAttachMenu(prev => !prev)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-all shrink-0"
                title="Прикрепить"
              >
                <Paperclip className="w-4 h-4" />
              </button>

              {showAttachMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowAttachMenu(false)} />
                  <div className="absolute bottom-full left-0 mb-2 w-52 bg-[#1a1a2e] border border-white/10 rounded-xl z-20 p-1.5 space-y-0.5 shadow-xl">
                    <button
                      onClick={() => { fileInputRef.current?.click(); setShowAttachMenu(false) }}
                      className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all"
                    >
                      <Upload className="w-4 h-4 text-indigo-400" />
                      <span>Загрузить файлы</span>
                    </button>
                    <button
                      onClick={() => { handleCaptureCamera(); setShowAttachMenu(false) }}
                      className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all"
                    >
                      <Camera className="w-4 h-4 text-purple-400" />
                      <span>Захват камерой</span>
                    </button>
                    <button
                      onClick={() => { handleAddPageLink(); setShowAttachMenu(false) }}
                      className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 transition-all"
                    >
                      <Link className="w-4 h-4 text-blue-400 whitespace-nowrap" />
                      <span>Добавить ссылку на страницу</span>
                    </button>
                  </div>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              multiple
              accept="*/*"
            />
            <input
              ref={fileImportRef}
              type="file"
              onChange={handleFileImport}
              className="hidden"
              accept=".json"
            />

            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Спроси ${character.name}...`}
              rows={1}
              className="flex-1 bg-transparent px-1 py-2 text-sm text-gray-200 placeholder-gray-600 resize-none max-h-[260px] outline-none"
            />

            <div className="flex items-center gap-1 pb-1 pr-1">
              <button
                onClick={() => setWebSearch(!webSearch)}
                className={`
                  p-2 rounded-lg transition-all
                  ${webSearch
                    ? 'bg-indigo-500/20 text-indigo-400'
                    : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                  }
                `}
                title="Поиск в интернете"
              >
                <Globe className="w-4 h-4" />
              </button>

              {isSending ? (
                <button
                  onClick={handleStop}
                  className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
                  title="Остановить"
                >
                  <StopCircle className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={(!input.trim() && files.length === 0)}
                  className="p-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-500 hover:to-purple-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Подсказка о загруженных файлах */}
        {files.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                <Upload className="w-3 h-3 text-indigo-400" />
                <span className="text-[10px] text-indigo-300 truncate max-w-[120px]">{f.name}</span>
                <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="p-0.5 hover:text-white text-indigo-400">
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        {/* Кнопка очистки диалога под полем */}
        <div className="flex items-center mt-2">
          <button
            onClick={handleClearDialogue}
            disabled={messages.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition-colors text-xs text-gray-400"
          >
            <Trash2 className="w-3 h-3" />
            Очистить диалог
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Main Characters Page
// ============================================================
export default function Characters() {
  const [characters, setCharacters] = useState<CharacterInfo[]>([])
  const [characterSessions, setCharacterSessions] = useState<Map<string, ChatSessionItem[]>>(new Map())
  const SELECTED_CHAR_KEY = 'selected_character_id'
  // Синхронно читаем id из localStorage при инициализации
  const savedCharId = typeof window !== 'undefined' ? localStorage.getItem(SELECTED_CHAR_KEY) : null
  const [selectedCharacter, setSelectedCharacter] = useState<CharacterInfo | null>(null)
  const [pendingCharId, setPendingCharId] = useState<string | null>(savedCharId)
  const selectedCharacterRef = useRef(selectedCharacter)
  const [editingCharacter, setEditingCharacter] = useState<CharacterInfo | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null)
  const { user } = useAuthStore()
  const isAdmin = user?.is_admin

  const loadCharacters = useCallback(async (retries = 3) => {
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const [charRes, chatRes] = await Promise.all([
          client.get('/characters'),
          client.get('/chat'),
        ])
        const chars: CharacterInfo[] = charRes.data || []
        const sessions: ChatSessionItem[] = chatRes.data || []

        const grouped = new Map<string, ChatSessionItem[]>()
        for (const s of sessions) {
          if (s.character_id) {
            const existing = grouped.get(s.character_id) || []
            existing.push(s)
            grouped.set(s.character_id, existing)
          }
        }

        setCharacters(chars)
        setCharacterSessions(grouped)
        setLoading(false)
        return
      } catch {
        if (attempt < retries - 1) {
          await new Promise(r => setTimeout(r, 1000))
        }
      }
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    loadCharacters()
  }, [loadCharacters])

  const handleDelete = async (id: string) => {
    try {
      await client.delete(`/characters/${id}`)
      toast.success('Персонаж удалён')
      loadCharacters()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Ошибка удаления')
    }
    setShowDeleteConfirm(null)
  }

  // Восстановление selectedCharacter после загрузки списка
  useEffect(() => {
    if (pendingCharId && characters.length > 0 && !selectedCharacter) {
      const found = characters.find(c => c.id === pendingCharId)
      if (found) {
        setSelectedCharacter(found)
      } else {
        localStorage.removeItem(SELECTED_CHAR_KEY)
      }
      setPendingCharId(null)
    }
  }, [characters, pendingCharId])

  // Синхронизируем ref и localStorage
  useEffect(() => {
    selectedCharacterRef.current = selectedCharacter
    if (selectedCharacter) {
      localStorage.setItem(SELECTED_CHAR_KEY, selectedCharacter.id)
    } else {
      localStorage.removeItem(SELECTED_CHAR_KEY)
    }
  }, [selectedCharacter])

  // Если есть selectedCharacter или pendingCharId — показываем чат
  if (selectedCharacter || pendingCharId) {
    // Пока список не загружен, но есть сохранённый id — показываем лоадер
    if (pendingCharId && !selectedCharacter) {
      return (
        <div className="flex items-center justify-center h-[calc(100vh-6rem)]">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
        </div>
      )
    }
    return (
      <CharacterChat
        character={selectedCharacter!}
        onBack={() => setSelectedCharacter(null)}
      />
    )
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      {/* Заголовок */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold gradient-text flex items-center gap-2">
            <Bot className="w-6 h-6" />
            ИИ Персонажи
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Общайтесь с уникальными AI-персонажами. Каждый — со своим характером и стилем общения.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 text-sm font-medium transition-all"
        >
          <Plus className="w-4 h-4" />
          Создать
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {characters.map((char, idx) => {
            const sessions = characterSessions.get(char.id) || []
            const bgGrad = COLORS[char.color] || COLORS.indigo

            return (
              <div
                key={char.id}
                className={`glass-card p-5 group cursor-pointer hover:scale-[1.02] transition-all duration-200 border ${bgGrad} relative`}
                onClick={() => setSelectedCharacter(char)}
                draggable={isAdmin}
                onDragStart={(e) => {
                  setDraggedIdx(idx)
                  e.dataTransfer.effectAllowed = 'move'
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'move'
                }}
                onDrop={async () => {
                  if (draggedIdx === null || draggedIdx === idx) return
                  const newChars = [...characters]
                  const [moved] = newChars.splice(draggedIdx, 1)
                  newChars.splice(idx, 0, moved)
                  setCharacters(newChars)
                  setDraggedIdx(null)
                  try {
                    for (let si = 0; si < newChars.length; si++) {
                      const newSort = (si + 1) * 1000
                      if (newChars[si].sort_order !== newSort) {
                        await client.put(`/characters/${newChars[si].id}`, { sort_order: newSort })
                      }
                    }
                  } catch {
                    console.warn('Не удалось сохранить порядок')
                  }
                }}
              >
                {/* Drag handle */}
                {isAdmin && (
                  <div
                    className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-white/10 transition-all cursor-grab active:cursor-grabbing"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <GripVertical className="w-3.5 h-3.5 text-gray-500" />
                  </div>
                )}

                <div className="flex items-start gap-4">
                  {char.avatar_url ? (
                    <img src={char.avatar_url} alt="" className="w-14 h-14 rounded-xl object-cover border border-white/10" />
                  ) : (
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${bgGrad} flex items-center justify-center text-2xl`}>
                      {char.emoji}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-semibold text-white group-hover:text-indigo-300 transition-colors">
                      {char.name}
                    </h2>
                    {char.bio && (
                      <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{char.bio}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <button className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors">
                        <MessageCircle className="w-3.5 h-3.5" />
                        Начать диалог
                      </button>
                      {sessions.length > 0 && (
                        <span className="text-[10px] text-gray-600">
                          {sessions.length} диалог{sessions.length > 1 ? 'а' : ''}
                        </span>
                      )}
                    </div>

                    {/* Actions: владелец или админ */}
                    {(isAdmin || char.is_owner) && (
                      <div
                        className="flex items-center gap-2 mt-2 pt-2 border-t border-white/5 opacity-0 group-hover:opacity-100 transition-all"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => setEditingCharacter(char)}
                          className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-indigo-300 transition-colors"
                        >
                          <Pencil className="w-3 h-3" />
                          Редактировать
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(char.id)}
                          className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                          Удалить
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {!loading && characters.length === 0 && (
        <div className="text-center py-12">
          <Bot className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Персонажи пока не добавлены</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-3 px-4 py-2 rounded-xl bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300 text-sm font-medium transition-all inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Создать персонажа
          </button>
        </div>
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-[#12122a] rounded-2xl border border-white/10 p-6">
            <h3 className="text-sm font-semibold text-white mb-2">Удалить персонажа?</h3>
            <p className="text-xs text-gray-400 mb-4">Это действие нельзя отменить. Все диалоги с этим персонажем останутся.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs text-gray-300 transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-xs text-red-300 transition-colors"
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editor/creator modal */}
      {(editingCharacter || showCreate) && (
        <CharacterEditor
          character={editingCharacter || null}
          onSave={() => {
            setEditingCharacter(null)
            setShowCreate(false)
            loadCharacters()
          }}
          onClose={() => {
            setEditingCharacter(null)
            setShowCreate(false)
          }}
        />
      )}
    </div>
  )
}
